import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Heading,
  Container,
  Spacer,
  Button,
  Icon,
  Flex,
  HStack,
  Stack,
  Center,
  Spinner,
  Box,
  Text,
  Tabs,
  useBreakpointValue,
} from '@chakra-ui/react';
import { PlusIcon, Bars3Icon, Squares2X2Icon, HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { NavLink, useParams } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useDebounce from '@/lib/hooks/use-debounce';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsUser from '@/lib/hooks/use-is-user';
import useIsMasterAdmin from '@/lib/hooks/use-is-master-admin';
import useCurrentUser from '@/lib/hooks/use-current-user';
import useContentTypesStore from '@/utils/use-content-type';
import useContentData from '@/components/content-list/elements/use-content-data';
import useContentState from '@/components/content-list/elements/use-content-state';
import Filters from '@/components/content-list/elements/filters';
import Sort from '@/components/content-list/elements/sort';
import Search from '@/components/content-list/elements/search';
import ContentList from './content-list';
import api, { baseApi } from '@/utils/api';
import { ContentType, FieldOption } from '@/lib/types';

interface FilterConfig {
  label: string;
  id: string;
  key: string;
  options: FieldOption[];
}

interface Partner {
  name: string;
  content_type_id: number;
  workspace_id: number;
  content_counts: number;
  reference_id: string;
}

export default function Contents() {
  const { workspaceId, contentTypeId, tabIndex } = useParams<{
    workspaceId: string;
    contentTypeId: string;
    tabIndex: string;
  }>();
  const isAdmin = useIsInAdminGroup();
  const isUser = useIsUser();
  const isMasterAdmin = useIsMasterAdmin();
  const isCancelled = useIsCancelled();
  const { user } = useCurrentUser();
  const { contentTypes } = useContentTypesStore();

  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [viewFavorites, setViewFavorites] = useState<boolean>(false);

  const [contentType, setContentType] = useState<ContentType>();
  const [filterConfig, setFilterConfig] = useState<FilterConfig[]>([]);
  const [facetFields, setFacetFields] = useState<string[]>([]);
  const [isBaseContentType, setIsBaseContentType] = useState<boolean>(false);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [referenceId, setReferenceId] = useState<string>();

  const initializingRef = useRef(false);
  const observer = useRef<IntersectionObserver | null>(null);

  const {
    filters,
    setFilters,
    selectListFilters,
    setSelectListFilters,
    searchQuery,
    setSearchQuery,
    sortKey,
    setSortKey,
    page,
    setPage,
    initialized,
    setInitialized,
    authors,
    setAuthors,
    resetState,
  } = useContentState();

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const fetchFields = useCallback(async () => {
    if (!referenceId) {
      setFilterConfig([]);
      setFacetFields([]);
      return;
    }

    try {
      setInitialized(false);

      const response = await baseApi.get(`public/content-type-fields/content-type/${referenceId}`);
      const fields = response.data.data || [];
      const config = [];
      const facetFields = [];

      for (const field of fields) {
        const selectItem = {
          label: field.field_name,
          id: field.field_id,
          key: `content.${field.referenceID}.value`,
          options: field.field_options,
        };
        config.push(selectItem);
        facetFields.push(selectItem.key);
      }

      setFilterConfig(config);
      setFacetFields(facetFields);
      setInitialized(true);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: 'Failed to fetch filters',
      });
      throw err;
    }
  }, [referenceId, setInitialized]);

  const myLibrary = useCallback(
    async (userId: number) => {
      if (!workspaceId || !contentTypeId) return;

      const contentType = [...contentTypes].find((type) => type.id === Number(contentTypeId));
      if (contentType) {
        setContentType(contentType);

        const isBase =
          Number(contentType.workspace_id) !== parseInt(workspaceId, 10) || isMasterAdmin === true;
        setIsBaseContentType(isBase);

        const refId = contentType?.workspace_content_type_reference_id || contentType?.reference_id;

        if (!refId) return;

        setReferenceId(refId);
      }
      resetState();
      setFilters([{ key: 'created_by', val: userId.toString() }]);
      fetchFields();
    },
    [contentTypes, workspaceId, contentTypeId, isMasterAdmin, setFilters, resetState, fetchFields],
  );

  const workspaceLibrary = useCallback(async () => {
    if (!workspaceId || !contentTypeId) return;

    const contentType = [...contentTypes].find((type) => type.id === Number(contentTypeId));
    if (contentType) {
      setContentType(contentType);
      const isBase =
        Number(contentType.workspace_id) !== parseInt(workspaceId, 10) || isMasterAdmin === true;
      setIsBaseContentType(isBase);

      const refId = contentType?.workspace_content_type_reference_id || contentType?.reference_id;

      if (!refId) return;

      setReferenceId(refId);
    }

    try {
      resetState();

      const res = await api.get(`/workspace/content-authors/${workspaceId}/${contentTypeId}`);
      setAuthors(res.data.data || []);
      setFilters([{ key: 'status', val: 'published' }]);
      fetchFields();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to load authors',
        duration: 3000,
      });
    }
  }, [
    contentTypes,
    workspaceId,
    contentTypeId,
    isMasterAdmin,
    resetState,
    setFilters,
    setAuthors,
    fetchFields,
  ]);

  const partnerLibraries = useCallback(async () => {
    if (!workspaceId || !contentTypeId) return;

    const contentType = [...contentTypes].find((type) => type.id === Number(contentTypeId));
    if (contentType) {
      setContentType(contentType);

      const isBase =
        Number(contentType.workspace_id) !== parseInt(workspaceId, 10) || isMasterAdmin === true;
      setIsBaseContentType(isBase);
    }

    try {
      resetState();
      setFilters([{ key: 'status', val: 'published' }]);

      const response = await api.get(`/workspace/public-selected-workspaces/${workspaceId}`);

      if (!response.data.data || response.data.data.length === 0) {
        setPartners([]);
        setReferenceId(undefined);
        setFilterConfig([]);
        setFacetFields([]);

        return;
      }

      const partners = response.data.data
        .map((data: any) => {
          const matchingContentType = data.content_types.find(
            (type: any) => type.content_type_id === Number(contentTypeId),
          );
          return {
            name: data.workspace_name,
            workspace_id: data.workspace_id,
            content_type_id: contentTypeId,
            content_counts: matchingContentType ? matchingContentType.content_counts : 0,
            reference_id:
              matchingContentType?.workspace_content_type_reference_id ||
              matchingContentType?.content_type_reference_id,
          };
        })
        .filter((partner: Partner) => partner.content_counts > 0);

      if (partners.length > 0) {
        setPartners(partners);
        setReferenceId(partners[0]?.reference_id);
        await fetchFields();
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err?.response?.data?.error || 'Failed to load partner content',
      });
    }
  }, [
    workspaceId,
    contentTypes,
    contentTypeId,
    isMasterAdmin,
    setFilters,
    resetState,
    fetchFields,
  ]);

  useEffect(() => {
    const initializeTabContent = async () => {
      if (!tabIndex || !user?.id || contentTypes.length === 0) return;

      if (initializingRef.current) return;
      initializingRef.current = true;

      try {
        switch (tabIndex) {
          case 'my-library':
            await myLibrary(user.id);
            break;
          case 'workspace-library':
            await workspaceLibrary();
            break;
          case 'partner-libraries':
            await partnerLibraries();
            break;
        }
      } finally {
        initializingRef.current = false;
      }
    };

    initializeTabContent();
  }, [contentTypes, contentTypeId, tabIndex, user]);

  useEffect(() => {
    if (referenceId) {
      fetchFields();
    }
  }, [referenceId, fetchFields]);

  const { contents, optionConfig, isLoading, hasMore, reloadContents } = useContentData({
    initialized,
    referenceId,
    filters,
    selectListFilters,
    facetFields,
    sortKey,
    searchQuery: debouncedSearchQuery,
    pageNumber: page,
  });

  const switchPartner = useCallback(
    async (partner: Partner) => {
      setReferenceId(partner.reference_id);
      resetState();
      setFilters([{ key: 'status', val: 'published' }]);
      setFilterConfig([]);
      setFacetFields([]);
    },
    [resetState, setFilters],
  );

  const lastContentElementRef = useCallback(
    (node: any) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage(page + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, page, setPage],
  );

  const handleFilterChange = useCallback(
    (newFilters: any) => {
      const formattedFilters = Object.entries(newFilters).map(([key, value]) => ({
        key: key,
        val: (value as string[]).join(','),
      }));
      setSelectListFilters(formattedFilters);
      setPage(1);
    },
    [setSelectListFilters, setPage],
  );

  const handleSortChange = useCallback(
    (value: string) => {
      setSortKey(value);
      setPage(1);
    },
    [setSortKey, setPage],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setPage(1);
    },
    [setSearchQuery, setPage],
  );

  const handleResetFilters = useCallback(() => {
    setSelectListFilters([]);
    setPage(1);
  }, [setSelectListFilters, setPage]);

  const toggleViewMode = useCallback((mode: 'list' | 'grid') => {
    setViewMode(mode);
  }, []);

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
      <Card.Header>
        <Container>
          <Flex mb={4}>
            <Heading size={'3xl'}>{contentType && contentType.name}</Heading>
            <Spacer />
            <Box display={{ base: 'none', md: 'block' }} w={'50%'}>
              <Search
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onClearSearch={() => setSearchQuery('')}
              />
            </Box>
            <Spacer />
            {(isAdmin || isUser) && !isCancelled && (
              <Button colorPalette='brand' asChild>
                <NavLink to={`/${workspaceId}/content-types/${contentTypeId}/contents/create`}>
                  <Icon as={PlusIcon} w={4} h={4} />
                  Create
                </NavLink>
              </Button>
            )}
          </Flex>
        </Container>
      </Card.Header>
      <Card.Body>
        <Container>
          <Stack gap={3}>
            <Tabs.Root
              value={tabIndex}
              orientation={isSmallScreen ? 'vertical' : 'horizontal'}
              variant={isSmallScreen ? 'enclosed' : 'line'}
              size='lg'
            >
              <Tabs.List width={isSmallScreen ? 'full' : 'auto'}>
                <Tabs.Trigger value='workspace-library' asChild>
                  <Button colorPalette='brand' variant='ghost' asChild>
                    <NavLink
                      to={`/${workspaceId}/content-types/${contentTypeId}/contents/workspace-library`}
                    >
                      Workspace Library
                    </NavLink>
                  </Button>
                </Tabs.Trigger>
                {isBaseContentType && (
                  <Tabs.Trigger value='partner-libraries' asChild>
                    <Button colorPalette='brand' variant='ghost'>
                      <NavLink
                        to={`/${workspaceId}/content-types/${contentTypeId}/contents/partner-libraries`}
                      >
                        Partner Libraries
                      </NavLink>
                    </Button>
                  </Tabs.Trigger>
                )}
                {(isAdmin || isUser) && (
                  <Tabs.Trigger value='my-library' asChild>
                    <Button colorPalette='brand' variant='ghost' asChild>
                      <NavLink
                        to={`/${workspaceId}/content-types/${contentTypeId}/contents/my-library`}
                      >
                        My Library
                      </NavLink>
                    </Button>
                  </Tabs.Trigger>
                )}
              </Tabs.List>
            </Tabs.Root>

            <Box display={{ base: 'block', md: 'none' }} w={'full'}>
              <Search
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onClearSearch={() => setSearchQuery('')}
              />
            </Box>
            {tabIndex === 'partner-libraries' && (
              <Stack gap={3} direction={'row'}>
                {partners.map((partner) => (
                  <Button
                    colorPalette='brand'
                    key={partner.workspace_id}
                    size={'sm'}
                    variant={referenceId === partner.reference_id ? 'solid' : 'outline'}
                    onClick={() => switchPartner(partner)}
                  >
                    {partner.name}
                  </Button>
                ))}
              </Stack>
            )}
            {contentTypeId && (
              <Box w={'full'}>
                <Filters
                  filterConfig={filterConfig}
                  optionConfig={optionConfig}
                  authors={authors}
                  filters={filters}
                  setFilters={setFilters}
                  onFilterChange={handleFilterChange}
                  onResetFilters={handleResetFilters}
                />
              </Box>
            )}
            <Stack gap={{ base: 0, md: 3 }} direction={'row'}>
              <Sort sortKey={sortKey} onSortChange={handleSortChange} />
              <Spacer />
              <HStack gap={{ base: 0, md: 3 }}>
                <Button
                  size='sm'
                  variant='ghost'
                  color={viewFavorites ? 'red.600' : 'gray.700'}
                  onClick={() => setViewFavorites(!viewFavorites)}
                >
                  <Icon as={viewFavorites ? HeartSolidIcon : HeartIcon} w={4} h={4} />
                  Favorities
                </Button>
                <Button
                  colorPalette='brand'
                  size='sm'
                  variant='ghost'
                  onClick={() => toggleViewMode(viewMode === 'list' ? 'grid' : 'list')}
                >
                  <Icon as={viewMode === 'list' ? Squares2X2Icon : Bars3Icon} w={4} h={4} />
                  {viewMode === 'list' ? 'Grid View' : 'List View'}
                </Button>
              </HStack>
            </Stack>

            {workspaceId && contentTypeId && (
              <ContentList
                contents={contents}
                workspaceId={workspaceId}
                contentTypeId={contentTypeId}
                viewMode={viewMode}
                viewFavorites={viewFavorites}
                filterConfig={filterConfig}
                lastContentElementRef={lastContentElementRef}
                reloadContents={reloadContents}
              />
            )}
            {isLoading && (
              <Center mt={4}>
                <Spinner size={'lg'} />
              </Center>
            )}
            {!isLoading && contents.length === 0 && (
              <Text fontSize={'sm'} textAlign={'center'}>
                No content available
              </Text>
            )}
          </Stack>
        </Container>
      </Card.Body>
    </Card.Root>
  );
}
